import { ReactNode } from "react";
import Image from "next/image";

import Sprite from "@/components/atoms/Sprite";
import {
  ImageFieldsFragment,
  ImageStyleAvailable,
} from "@/graphql/sdk/__generated__";
import { cn } from "@/helpers/className";
import { getVariation } from "@/helpers/image";

interface HeroProps {
  className?: string;
  headerTopAddon?: ReactNode;
  image?: {
    url: string;
  };
  media?: ImageFieldsFragment;
  noSprite?: boolean;
  small?: boolean;
  title: string;
  titleClassName?: string;
}

export const Hero = ({
  className,
  headerTopAddon,
  image,
  media,
  noSprite,
  small,
  title,
  titleClassName,
}: HeroProps) => {
  if (!image) {
    image = media?.variations
      ? getVariation(media.variations, ImageStyleAvailable.Hero)
      : undefined;
  }

  return (
    <div
      className={cn(
        "default-hero relative z-0",
        small
          ? "flex items-center justify-center px-4 py-16"
          : "flex items-end justify-center px-4 py-6 lg:py-20",
        !image && "relative overflow-hidden bg-yellow-600",
        small
          ? "min-h-[149px] sm:min-h-[154px] md:min-h-[162px] lg:min-h-[171px] xl:min-h-[179px] 2xl:min-h-[179px]"
          : "min-h-[215px] sm:min-h-[215px] md:min-h-[288px] lg:min-h-[400px] xl:min-h-[400px] 2xl:min-h-[440px]",
        small
          ? "flex items-center justify-center"
          : "flex items-end justify-center",
        className,
      )}
      style={image && { backgroundColor: "rgba(0, 0, 0, 0.3)" }}
    >
      {image ? (
        <Image
          className="inset-0 -z-1 h-full w-full object-cover mix-blend-multiply xl:absolute"
          src={image.url}
          alt={media?.alt ?? title ?? ""}
          fill
          loading="eager"
          fetchPriority="high"
          priority
        />
      ) : (
        <>
          {!noSprite && (
            <Sprite className="right-0 top-1/2 mt-12 w-[517px] -translate-y-1/2 translate-x-[66px] transition-none" />
          )}
        </>
      )}

      <div className="container relative z-1 flex flex-col items-center justify-center gap-6 lg:gap-8">
        {headerTopAddon}
        <h1
          className={cn(
            "!mb-0 break-words text-center",
            image && "text-white",
            titleClassName,
          )}
        >
          {title}
        </h1>
      </div>
    </div>
  );
};
